import { useAtom, getDefaultStore } from "jotai";

import { ChatMessage } from "../../models/chat/ChatMessage";
import { sendUserInputToChatbot } from "../server/chatbotUtils";
import { loadingChatMessageState } from "../../state/chat/LoadingChatMessageState";
import { chatMessagesState } from "../../state/chat/ChatMessagesState";
import { suggestionsState } from "../../state/chat/SuggestionsState";
import { chatbotConfigState } from "../../state/config/ChatbotConfigState";
import { chatIdState } from "../../state/chat/ChatIdState";
import { TRANSLATIONS } from "../constants/translations";
import {
  getAuthToken,
  getActiveChatbotId,
  getPublicWidgetSession,
} from "../server/authUtils";


function getActiveTranslation() {
  try {
    const store = getDefaultStore();
    const cfg = store.get(chatbotConfigState);
    const lang = (cfg?.language || "de").toLowerCase().startsWith("en") ? "en" : "de";
    return TRANSLATIONS[lang] || TRANSLATIONS.de;
  } catch (e) {
    return TRANSLATIONS.de;
  }
}

export function useChatState() {
  const [isLoading, setIsLoading] = useAtom(loadingChatMessageState);
  const [chatMessages, setChatMessages] = useAtom(chatMessagesState);
  const [suggestions, setSuggestions] = useAtom(suggestionsState);
  const [chatId, setChatId] = useAtom(chatIdState);

  return {
    isLoading,
    setIsLoading,
    chatMessages,
    setChatMessages,
    suggestions,
    setSuggestions,
    chatId,
    setChatId,
  };
}

export async function sendMessageToChatbot(message, state, imagePayload = null, options = {}) {
  const {
    isLoading,
    setIsLoading,
    chatMessages,
    setChatMessages,
    setSuggestions,
    chatId,
  } = state;

  if (isLoading) return;

  const trimmedMessage = String(message || "").trim();
  if (!trimmedMessage && !imagePayload) return;

  const newMessage = ChatMessage.userMessage(trimmedMessage || getActiveTranslation().imageUploadLabel);
  if (imagePayload) {
    newMessage.imagePreview = imagePayload;
  }

  setChatMessages((prev) => [...prev, newMessage]);
  setIsLoading(true);
  setSuggestions([]);

  try {
    const publicSession = getPublicWidgetSession();
    const isPublic = Boolean(options.publicVisitorToken || publicSession?.token);

    const userAuthToken = isPublic ? null : await getAuthToken();
    const chatbotId = options.chatbotId || getActiveChatbotId() || null;

    const firstUserIdx = chatMessages.findIndex((item) => (item.sender || item.role) === "user");
    const historyForModel = firstUserIdx >= 0 ? chatMessages.slice(firstUserIdx) : [];

    const result = await sendUserInputToChatbot({
      chatId,
      inputMessage: trimmedMessage,
      image: imagePayload,
      userAuthToken,
      publicVisitorToken: options.publicVisitorToken || publicSession?.token || null,
      publicBotId: options.publicBotId || publicSession?.publicBotId || null,
      chatbotId,
      pastMessages: historyForModel.map((msg) => ({
        role: msg.sender || msg.role,
        content: msg.message,
      })),
    });

    const media = Array.isArray(result.media) ? result.media : [];

    const botMessage = ChatMessage.botMessage(result.aiResponse, media, result.debugSources);
    setSuggestions(result.suggestions ?? []);
    setChatMessages((prev) => [...prev, botMessage]);
  } catch (error) {
    console.error("Error sending message:", error);
    setSuggestions([]);
    setChatMessages((prev) => [...prev, ChatMessage.errorMessage()]);
  } finally {
    setIsLoading(false);
  }
}
